import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../auth/contexts/AuthenticationContext';
import { classifyPendingActionError } from '../../shared/api/errors';
import { invalidateAll, INVITE_CLAIM_INVALIDATION_KEYS } from '../../shared/api/cacheInvalidation';
import { REPORTS_OWN_ERRORS } from '../../shared/api/callSiteErrors';

import {
  organizationApi, billingApi, orgBillingApi, employeeAnalyticsApi,
  inviteLinksApi, orgCreationRequestApi,
} from './endpoints';
import type { CoursePackage, OrgMember, PackageEnrollment, InviteLink, ClaimInviteLinkResponse } from './endpoints';
import { readInviteToken, clearInviteToken } from './pendingInviteClaim';

export type { OrgCreationRequest, OrgCreationRequestParams } from './endpoints';

export function useMyOrgCreationRequests(enabled = true) {
  return useQuery({
    queryKey: ['organization_creation_requests', 'mine'],
    queryFn: orgCreationRequestApi.mine,
    enabled,
  });
}

export function useCreateOrgCreationRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: orgCreationRequestApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization_creation_requests', 'mine'] });
    },
  });
}

export type { InviteLink, PublicInviteLink, ClaimInviteLinkResponse } from './endpoints';

export type {
  Organization, OrgMember, CoursePackage, PackageEnrollment, PackageEventRegistration,
  AnalyticsResponse, EmployeeStat, MemberEnrollment, MemberRegistration, ActivityEvent,
} from './endpoints';

export interface PackageSeatState {
  assignedUserIds: Set<number>;
  availableMembers: OrgMember[];
  isEventPackage: boolean;
}

export function usePackageSeatState(pkg: CoursePackage, members: OrgMember[]): PackageSeatState {
  return useMemo(() => {
    const assignedUserIds = new Set<number>([
      ...pkg.enrollments.map((e) => e.user.id),
      ...pkg.event_registrations.map((r) => r.user.id),
    ]);
    const availableMembers = members.filter(
      (m) => m.status === 'active' && !assignedUserIds.has(m.user.id),
    );
    const isEventPackage = pkg.course.delivery_type === 'event' || pkg.course.delivery_type === 'hybrid';
    return { assignedUserIds, availableMembers, isEventPackage };
  }, [pkg, members]);
}

export function canRevokeEnrollment(enrollment: PackageEnrollment): boolean {
  return enrollment.progress_percentage === 0 && enrollment.status !== 'completed';
}

export function useOrganizations() {
  return useQuery({ queryKey: ['organizations'], queryFn: organizationApi.list });
}

export function useOrganization(id: number | string | null | undefined) {
  return useQuery({
    queryKey: ['organizations', id],
    queryFn: () => {
      if (!id) throw new Error('useOrganization requires an id');
      return organizationApi.get(id);
    },
    enabled: !!id,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: organizationApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUpdateOrganization(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name?: string; billing_email?: string }) => organizationApi.update(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations', id] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useInviteMember(orgId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { email: string; role?: string; course_ids?: number[]; event_ids?: number[] }) =>
      organizationApi.invite(orgId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations', orgId] });
      queryClient.invalidateQueries({ queryKey: ['course_packages', orgId] });
    },
  });
}

export function useUpdateMember(orgId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role, first_name, last_name, email_address }: { memberId: number; role?: string; first_name?: string; last_name?: string; email_address?: string }) =>
      organizationApi.updateMember(orgId, memberId, { role, first_name, last_name, email_address }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organizations', orgId] }),
  });
}

export function useRemoveMember(orgId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: number) => organizationApi.removeMember(orgId, memberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organizations', orgId] }),
  });
}

export function useResendInvitation(orgId: number | string) {
  return useMutation({
    mutationFn: (memberId: number) => organizationApi.resendInvitation(orgId, memberId),
  });
}

export function useCreatePortal(orgId: number | string) {
  return useMutation({
    mutationFn: () => billingApi.createPortal(orgId),
    onSuccess: (data) => { window.location.href = data.url; },
  });
}

export function useOrgCourseCheckout(orgId: number | string, courseId: number | string) {
  return useMutation({
    mutationFn: (params: { user_id: number; discount_code?: string }) =>
      orgBillingApi.courseCheckout(orgId, courseId, params),
    onSuccess: (data) => { window.location.href = data.url; },
  });
}

/**
 * `reportsOwnErrors` is per call site, not baked into the hook: CoursePackages-
 * Section renders its own inline failure, while BuyGroupPackagePage has no
 * error surface at all and needs the app-wide toast to stay switched on.
 */
export function useGroupCheckout(
  orgId: number | string,
  courseId: number | string,
  opts: { reportsOwnErrors?: boolean } = {},
) {
  return useMutation({
    mutationFn: (params: { seats: number; discount_code?: string }) =>
      orgBillingApi.groupCheckout(orgId, courseId, params),
    meta: opts.reportsOwnErrors ? REPORTS_OWN_ERRORS : undefined,
    onSuccess: (data) => { window.location.href = data.url; },
  });
}

// Specific-date group package: N seats earmarked to one event_id.
export function useEventGroupCheckout(orgId: number | string, eventId: number | string) {
  return useMutation({
    mutationFn: (params: { seats: number; discount_code?: string; generate_invite_link?: boolean }) =>
      orgBillingApi.eventGroupCheckout(orgId, eventId, params),
    onSuccess: (data) => { window.location.href = data.url; },
  });
}

/** Invoice / PO group purchase. No redirect — the package + unpaid invoice
 *  are created synchronously; on success we refetch the packages list so
 *  the new seat pool shows up immediately. */
export function useGroupOfflineCheckout(orgId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      mode: 'course' | 'event';
      targetId: number | string;
      payment_method: 'invoice' | 'purchase_order';
      seats: number;
      po_number?: string;
      billing_email?: string;
      billing_name?: string;
      billing_phone?: string;
    }) => {
      const { mode, targetId, ...rest } = params;
      return orgBillingApi.groupOfflineCheckout(orgId, mode, targetId, rest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course_packages', orgId] });
    },
  });
}

export function useCoursePackages(orgId: number | string | null | undefined) {
  return useQuery({
    queryKey: ['course_packages', orgId],
    queryFn: () => {
      if (!orgId) throw new Error('useCoursePackages requires an orgId');
      return orgBillingApi.coursePackages(orgId);
    },
    enabled: !!orgId,
  });
}

export function useAssignSeat(orgId: number | string, packageId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { user_id: number; event_id?: number }) =>
      orgBillingApi.assignSeat(orgId, packageId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course_packages', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useDeleteOrganization(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => organizationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

/** Opt into `reportsOwnErrors` only where the failure is rendered inline —
 *  PackageAssignments.tsx shows nothing, so the app-wide toast is its only
 *  signal that the seat was not released. */
export function useUnassignSeat(
  orgId: number | string,
  packageId: number | string,
  opts: { reportsOwnErrors?: boolean } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => orgBillingApi.unassignSeat(orgId, packageId, userId),
    meta: opts.reportsOwnErrors ? REPORTS_OWN_ERRORS : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course_packages', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
}

export function useInviteLinks(orgId: number | string | null | undefined) {
  return useQuery<InviteLink[]>({
    queryKey: ['invite_links', orgId],
    queryFn: () => {
      if (!orgId) throw new Error('useInviteLinks requires an orgId');
      return inviteLinksApi.list(orgId);
    },
    enabled: !!orgId,
  });
}

export function useCreateInviteLink(orgId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { course_id?: number; course_slug?: string; course_package_id?: number; seat_cap: number; expires_at?: string | null }) =>
      inviteLinksApi.create(orgId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite_links', orgId] });
    },
  });
}

export function useRevokeInviteLink(orgId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => inviteLinksApi.revoke(orgId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite_links', orgId] });
    },
  });
}

/** HTTP status of a failed request, or null when the call never got a response
 *  (offline, DNS, CORS, corporate proxy). Used to tell "this link is dead" apart
 *  from "we couldn't reach the API", which must not render the same way. */
export function inviteLinkErrorStatus(error: unknown): number | null {
  return (error as { response?: { status?: number } })?.response?.status ?? null;
}

export function usePublicInviteLink(token: string | null | undefined) {
  return useQuery({
    queryKey: ['invite_links', 'public', token],
    queryFn: () => {
      if (!token) throw new Error('usePublicInviteLink requires a token');
      return inviteLinksApi.preview(token);
    },
    enabled: !!token,
    // A 404 is a real answer — retrying just delays the message. Everything
    // else (429 from a shared corporate egress IP, 5xx, a dropped connection)
    // is transient and worth another go before we tell the user anything.
    retry: (failureCount, error) => {
      const status = inviteLinkErrorStatus(error);
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

// Fires the invite-link claim once after auth is ready if a token is queued
// from the /invite/:token landing page. Mounted globally by InviteClaimBanner,
// which explains why; callers pass onClaimed/onError so a failure reaches the
// learner instead of being swallowed.
export function usePendingInviteLinkClaim(
  onClaimed?: (result: ClaimInviteLinkResponse) => void,
  onError?: (message: string) => void,
) {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuth();
  const firedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || firedRef.current) return;
    const token = readInviteToken();
    if (!token) return;

    firedRef.current = true;
    inviteLinksApi
      .claim(token)
      .then((result) => {
        clearInviteToken();
        invalidateAll(queryClient, INVITE_CLAIM_INVALIDATION_KEYS);
        onClaimed?.(result);
      })
      .catch((err: unknown) => {
        const { definitive, message } = classifyPendingActionError(err, {
          definitiveFallback: 'We could not add your course automatically. Please contact your administrator.',
          unreachable: "We couldn't reach the Human Risk Academy to claim your seat. Check your connection and refresh this page.",
        });
        if (definitive) clearInviteToken();
        else firedRef.current = false;
        onError?.(message);
      });
  }, [isAuthenticated, isLoading, queryClient, onClaimed, onError]);
}

export function useEmployeeAnalytics(orgId: number | string | null | undefined) {
  return useQuery({
    queryKey: ['employee_analytics', orgId],
    queryFn: () => {
      if (!orgId) throw new Error('useEmployeeAnalytics requires an orgId');
      return employeeAnalyticsApi.get(orgId);
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}
